import { useState, useEffect } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { cerrarSesion } from "../../store/slices/usuario.slice";
import { NotificationItem } from "./NotificacionItem";
import { Button } from "../atoms/Button";
import { getAllPersonas } from "../../services/persona.service";
import { toast } from "react-toastify";
import { BASE_URL } from "../../utils/configAxios";
import { getImagePerfil } from "../../services/image.service";
import { getAllPersonas as getAllPersonasSvc } from "../../services/persona.service";
import {
  getMisNotificaciones,
  marcarNotificacionComoLeida,
} from "../../services/notificacion.service";
import { Bell, LogOut, UserCircle } from "lucide-react";

export const UsuarioMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notificacionesOpen, setNotificacionesOpen] = useState(false);
  const [fotoPerfil, setFotoPerfil] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { nombre, apellido } = useSelector((store) => store.usuario);
  const userInfo = JSON.parse(localStorage.getItem("userInfoES") || "{}");
  const { rol, id } = userInfo;

  const puedeVerNotificaciones = ["ADM", "SADM", "EST"].includes(rol);
  const esEstudiante = rol === "EST";

  const getMissingFlagKey = (personaId) =>
    personaId ? `perfil_image_missing_${personaId}` : null;

  const isImageMarkedMissing = (personaId) => {
    const key = getMissingFlagKey(personaId);
    if (!key) return false;
    return localStorage.getItem(key) === "true";
  };

  const markImageAsMissing = (personaId) => {
    const key = getMissingFlagKey(personaId);
    if (!key) return;
    localStorage.setItem(key, "true");
  };

  const clearMissingMark = (personaId) => {
    const key = getMissingFlagKey(personaId);
    if (!key) return;
    localStorage.removeItem(key);
  };

  const cacheKeys = (usuarioId, personaId) => [
    usuarioId ? `perfil_image_${usuarioId}` : null,
    personaId ? `perfil_image_persona_${personaId}` : null,
    personaId ? `perfil_image_${personaId}` : null // compatibilidad legacy
  ].filter(Boolean);

  const readImageCache = (usuarioId, personaId) => {
    try {
      for (const key of cacheKeys(usuarioId, personaId)) {
        const imageData = localStorage.getItem(key);
        if (!imageData) continue;

        const parsed = JSON.parse(imageData);
        const cachedImage =
          typeof parsed === "string" ? parsed : parsed?.image;

        if (typeof cachedImage !== "string" || cachedImage.trim() === "") {
          localStorage.removeItem(key);
          continue;
        }

        if (cachedImage.startsWith("blob:")) {
          localStorage.removeItem(key);
          continue;
        }

        return {
          image: cachedImage,
          fileName: parsed?.fileName || null,
          rutaBD: parsed?.rutaBD || null,
        };
      }
    } catch (error) {
      console.error("Error al cargar imagen cacheada:", error);
    }
    return null;
  };

  const writeImageCache = (usuarioId, personaId, data) => {
    const payload = JSON.stringify(data);
    cacheKeys(usuarioId, personaId).forEach((key) => localStorage.setItem(key, payload));
    window.dispatchEvent(new CustomEvent("imageUpdated"));
  };

  useEffect(() => {
    const cargarFotoPerfil = async () => {
      try {
        // Obtener persona para conocer idPersona
        const personas = await getAllPersonas();
        const personaEncontrada = personas.find((p) => p.usuarioid === id);
        const idPersona = personaEncontrada?.idPersona;

        // 1) Intentar localStorage con claves consistentes
        const localImageData = readImageCache(id, idPersona);
        const localImage = localImageData?.image || null;
        const localFileName = localImageData?.fileName || null;
        let rutaImagenReferencia =
          localImageData?.rutaBD || personaEncontrada?.imagen || null;

        if (localImage) {
          setFotoPerfil(localImage);
          clearMissingMark(idPersona);
          // Intento en segundo plano para refrescar desde backend si hay fileName
          if (localFileName || rutaImagenReferencia) {
            try {
              let fileName = localFileName;
              if (!fileName && rutaImagenReferencia) {
                const partes = String(rutaImagenReferencia).split("/");
                fileName = partes[partes.length - 1];
              }
              if (!fileName || isImageMarkedMissing(idPersona)) return;

              setTimeout(async () => {
                try {
                  if (isImageMarkedMissing(idPersona)) return;
                  const url = await getImagePerfil(fileName);
                  if (url) {
                    setFotoPerfil(url);
                    clearMissingMark(idPersona);
                    writeImageCache(id, idPersona, {
                      idUsuario: id,
                      idPersona,
                      image: url,
                      fileName,
                      rutaBD:
                        rutaImagenReferencia ||
                        (personaEncontrada?.imagen ?? `images/perfil/${fileName}`),
                      uploadDate: new Date().toISOString()
                    });
                  }
                } catch (err) {
                  // Si es 404, no intentar más - el archivo no existe
                  if (err.isNotFound || err.status === 404) {
                    // Silenciar - ya sabemos que no existe
                    return;
                  }
                  if (err.isCorsError) {
                    console.warn("UsuarioMenu: Error de CORS al refrescar imagen, no se reintenta hasta recargar.");
                    return;
                  }
                  // Para otros errores, silenciar también (es un intento en segundo plano)
                }
              }, 2000);
            } catch {}
          }
          return;
        }

        // 2) Intentar backend con getImagePerfil
        if (
          (personaEncontrada?.imagen || localFileName) &&
          !isImageMarkedMissing(idPersona)
        ) {
          try {
            let fileName = localFileName;
            let rutaBD = rutaImagenReferencia || personaEncontrada?.imagen || null;
            if (!fileName && rutaBD) {
              const partes = String(rutaBD).split("/");
              fileName = partes[partes.length - 1];
            }
            if (!fileName) {
              setFotoPerfil(localImage || null);
              return;
            }

            const url = await getImagePerfil(fileName);
            setFotoPerfil(url);
            clearMissingMark(idPersona);
            writeImageCache(id, idPersona, {
              idUsuario: id,
              idPersona,
              image: url,
              fileName,
              rutaBD: rutaBD || `images/perfil/${fileName}`,
              uploadDate: new Date().toISOString(),
            });
            return;
          } catch (err) {
            // Si es 404, el archivo no existe - no usar fallback de URL absoluta
            if (err.isNotFound || err.status === 404) {
              markImageAsMissing(idPersona);
              setFotoPerfil(localImage || null);
              return;
            }
            if (err.isCorsError) {
              console.warn("UsuarioMenu: Error de CORS al cargar imagen, se mantiene caché/local.");
              return;
            }
            // Para otros errores, intentar URL absoluta como fallback
            try {
              const rutaBD =
                localImageData?.rutaBD || personaEncontrada?.imagen;
              if (!rutaBD) {
                setFotoPerfil(localImage || null);
                return;
              }
              const normalizada = rutaBD.startsWith("http")
                ? rutaBD
                : `${BASE_URL}/${rutaBD.replace(/^\/+/, "")}`;
              setFotoPerfil(normalizada);
              clearMissingMark(idPersona);
              writeImageCache(id, idPersona, {
                idUsuario: id,
                idPersona,
                image: normalizada,
                fileName: localFileName || null,
                rutaBD,
                uploadDate: new Date().toISOString(),
              });
              return;
            } catch {
              setFotoPerfil(localImage || null);
            }
          }
        }

        // 3) Sin imagen
        setFotoPerfil(null);
      } catch (error) {
        console.error("Error al cargar imagen de perfil:", error);
      }
    };

    cargarFotoPerfil();
    if (puedeVerNotificaciones) cargarNotificaciones();

    // Listener para actualizar imagen cuando se cambie
    const handleImageUpdate = () => {
      cargarFotoPerfil();
    };

    window.addEventListener('imageUpdated', handleImageUpdate);
    
    return () => {
      window.removeEventListener('imageUpdated', handleImageUpdate);
    };
  }, []);

  const cargarNotificaciones = async () => {
    try {
      const data = await getMisNotificaciones();
      const ordenadas = data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
      setNotifications(ordenadas);
    } catch (error) {
      toast.error("Error al obtener notificaciones.");
    }
  };

  const toggleMenu = () => setIsOpen(!isOpen);
  const toggleNotificaciones = () => setNotificacionesOpen(!notificacionesOpen);

  const handleLogout = () => {
    dispatch(cerrarSesion());
    navigate("/");
  };

  const notReadCount = notifications.filter((n) => !n.leido).length;

  const handleMarkAllAsRead = async () => {
    try {
      const promesas = notifications.filter(n => !n.leido).map(n => marcarNotificacionComoLeida(n.id));
      await Promise.all(promesas);
      toast.success("Notificaciones marcadas como leídas.");
      cargarNotificaciones();
    } catch (error) {
      toast.error("Error al marcar notificaciones.");
    }
  };

  const handleRefetchNotifications = () => {
    cargarNotificaciones();
  };

  return (
    <div className="flex items-center relative z-50">
      {/* Icono Notificaciones */}
      {puedeVerNotificaciones && (
        <div className="relative">
          <button
            onClick={toggleNotificaciones}
            className="relative w-12 h-12 flex items-center justify-center rounded-full hover:bg-gray-700 transition"
          >
            <Bell className="w-7 h-7 text-yellow-500 fill-yellow-500" />
            {notReadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full px-[6px] border-2 border-white leading-tight">
                {notReadCount}
              </span>
            )}
          </button>

          {notificacionesOpen && (
            <div className="absolute top-full right-0 bg-white shadow-lg rounded-md w-80 mt-2 pt-2">
              <div className="flex items-center justify-between px-3 mb-2">
                <h3 className="text-gray-800 text-base font-semibold">Notificaciones</h3>
                <div className="flex items-center gap-2">
                  {notReadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="text-sm text-gray-500 hover:underline"
                    >
                      Marcar todas como leídas
                    </button>
                  )}
                  <button
                    onClick={handleRefetchNotifications}
                    className="hover:text-blue-600 transition"
                  >
                    <ArrowPathIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="max-h-60 overflow-y-auto">
                {notifications.length > 0 ? (
                  notifications.map((notif) => (
                    <NotificationItem
                      key={notif.id}
                      notification={notif}
                      onLeido={cargarNotificaciones}
                    />
                  ))
                ) : (
                  <p className="px-3 py-2 text-sm text-gray-700">No hay notificaciones</p>
                )}
              </div>

              {rol !== "EST" && (
                <div className="border-t border-gray-200 px-3 pt-2">
                  <button
                    onClick={() => {
                      setNotificacionesOpen(false);
                      navigate("/notificaciones");
                    }}
                    className="w-full text-center text-sm text-blue-600 hover:underline"
                  >
                    Ver todas las notificaciones →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Foto o Iniciales de Usuario */}
      <Button
        onClick={toggleMenu}
        title={`${nombre} ${apellido}`}
        className="w-14 h-14 bg-white border-2 border-white p-0 rounded-full overflow-hidden shadow-lg transition-transform duration-200 hover:scale-105 ml-4"
      >
        {fotoPerfil ? (
          <img src={fotoPerfil} alt="Foto de perfil" className="w-full h-full object-cover" />
        ) : (
          <span className="text-blue-800 text-xl font-bold uppercase flex items-center justify-center w-full h-full">
            {nombre?.[0]}{apellido?.[0]}
          </span>
        )}
      </Button>

      {isOpen && (
        <ul className="user-dropdown absolute top-full bg-white right-3 rounded-sm flex flex-col mt-2 shadow-md text-slate-600 font-medium min-w-[220px] whitespace-nowrap">
          <li
            onClick={() => {
              if (rol === "EST") navigate("/perfilEstudiante");
              else if (rol === "ADM" || rol === "SADM") navigate("/perfilAdm");
              else if (rol === "DOC") navigate("/perfilDocente");
            }}
            className="transition-colors cursor-pointer hover:bg-slate-200 py-2 px-6 flex items-center gap-2"
          >
            <UserCircle className="w-5 h-5 text-gray-500" />
            <span>Mi perfil</span>
          </li>
          {puedeVerNotificaciones && rol !== "EST" && (
            <li
              onClick={() => navigate("/notificaciones")}
              className="transition-colors cursor-pointer hover:bg-slate-200 py-2 px-6 flex items-center gap-2"
            >
              <Bell className="w-5 h-5 text-gray-500" />
              <span>Ver notificaciones</span>
            </li>
          )}

          <li
            onClick={handleLogout}
            className="transition-colors cursor-pointer hover:bg-slate-200 py-2 px-6 flex items-center gap-2"
          >
            <LogOut className="w-5 h-5 text-gray-500" />
            <span>Cerrar sesión</span>
          </li>
        </ul>
      )}
    </div>
  );
};



